import classNames from 'classnames';
import type { ReactNode } from 'react';
import React, { forwardRef } from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import { Link } from '../../../../utils/router/v3Bridge';
import Caret from '../../../../components/Caret';
import SVGIcon from '../../../../components/SVGIcon';
import Tooltip from '../../../../components/Tooltip';
import addTestId from '../../../../utils/addTestId';
import ClickOutsideHandler from '../../../../utils/ClickOutsideHandler';
import isExternalUrl from '../../../../utils/url/isExternalUrl';
import styles from './ToolbarButton.module.scss';

interface ToolbarButtonGroupProps {
  children: ReactNode;
}

export const ToolbarButtonGroup = ({ children }: ToolbarButtonGroupProps) => (
  <div className={styles.group}>{children}</div>
);

interface ToolbarButtonProps {
  icon?: ReactNode;
  active?: boolean;
  disabled?: boolean;
  tooltip?: ReactNode;
  popOver?: (onDismiss: () => void) => ReactNode;
  popOverWidth?: 'auto' | 'full';
  popOverHideCaret?: boolean;
  popOverOpen?: boolean;
  onPopOverOpenChange?: (open: boolean) => void;
  rightAlignPopOver?: boolean;
  children?: ReactNode;
  onClick?: () => void;
  to?: string;
  target?: string;
  testId?: string;
  buttonType?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
}

const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  (
    {
      icon,
      active,
      disabled,
      tooltip,
      popOver,
      popOverWidth = 'auto',
      popOverHideCaret,
      popOverOpen,
      onPopOverOpenChange,
      rightAlignPopOver,
      children,
      onClick,
      to,
      target,
      testId,
      buttonType = 'button',
    }: ToolbarButtonProps,
    ref,
  ) => {
    const caret = popOver && !popOverHideCaret && (popOverOpen ? 'up' : 'down');
    const iconNode = icon && (
      <span className={styles.icon}>
        {typeof icon === 'string' ? (
          <SVGIcon inline size="small">
            {icon}
          </SVGIcon>
        ) : (
          icon
        )}
      </span>
    );

    let button = (
      <button
        ref={ref}
        // eslint-disable-next-line react/button-has-type
        type={buttonType}
        disabled={disabled}
        onClick={() => {
          if (onClick) {
            onClick();
          }
          if (popOver) {
            onPopOverOpenChange?.(!popOverOpen);
          }
        }}
        className={classNames(styles.button, {
          [styles.active]: active,
          [styles.selected]: popOver && popOverOpen,
        })}
        {...addTestId(testId)}
      >
        {iconNode}
        {!!children && <div className={styles.label}>{children}</div>}
        {caret && (
          <div className={styles.caret}>
            <Caret direction={caret} />
          </div>
        )}
      </button>
    );

    if (to) {
      button = isExternalUrl(to) ? (
        <a className={styles.link} href={to} target={target}>
          {button}
        </a>
      ) : (
        <Link className={styles.link} to={to} target={target}>
          {button}
        </Link>
      );
    }

    if (tooltip) {
      button = (
        <Tooltip
          oneLine
          display="block"
          tooltip={tooltip}
          placement="top"
          trigger="hover"
          modifiers={[{ name: 'offset', options: { offset: [0, 2] } }]}
        >
          {button}
        </Tooltip>
      );
    }

    return (
      <div className={styles.container}>
        {popOver ? (
          <ClickOutsideHandler
            onClickOutside={() => popOverOpen && onPopOverOpenChange?.(false)}
          >
            <Manager>
              <Reference>
                {({ ref: referenceRef }) => (
                  <div ref={referenceRef}>{button}</div>
                )}
              </Reference>
              {popOverOpen && (
                <Popper
                  placement={rightAlignPopOver ? 'bottom-end' : 'bottom-start'}
                  modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                >
                  {({ ref: popOverRef, style }) => (
                    <div
                      className={classNames(styles.popOver, {
                        [styles.full]: popOverWidth === 'full',
                      })}
                      ref={popOverRef}
                      style={style}
                    >
                      <div
                        className={classNames(styles.container, {
                          [styles.rightAlign]: rightAlignPopOver,
                        })}
                      >
                        {popOver(() => {
                          onPopOverOpenChange?.(false);
                        })}
                      </div>
                    </div>
                  )}
                </Popper>
              )}
            </Manager>
          </ClickOutsideHandler>
        ) : (
          button
        )}
      </div>
    );
  },
);

export default ToolbarButton;
